import {MipLevelRegion, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {HalImage, HalImageRef} from "@cm/hal"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {definition, Kernel} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convolve/definition"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {applyBorderModeToRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convolve/utils"
import {deepEqual, isPromise} from "@cm/utils/misc"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpConvolveHal extends ImageOpHal<Definition, ImageOpConvolveHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpConvolveHalData>): ImageOpConvolveHalData {
        return new ImageOpConvolveHalData({
            ...args,
            descriptor: args.parameters.sourceImage.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }
}

class ImageOpConvolveHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpConvolveHalData>) {
        super(args)
        if (args.prevData && deepEqual(args.prevData.parameters.kernel, args.parameters.kernel)) {
            if (isPromise(args.prevData.kernelByMipLevel)) {
                args.prevData.kernelByMipLevel = args.prevData.kernelByMipLevel.then((kernelByMipLevel) => kernelByMipLevel.map((k) => k.addRef())) // reassign the addRef back to the source, so in case of overlapping releaseKernel call it gets addRefed first and then released
                this.kernelByMipLevel = args.prevData.kernelByMipLevel
            } else {
                this.kernelByMipLevel = args.prevData.kernelByMipLevel.map((k) => k.addRef())
            }
        }
    }

    protected override dispose() {
        this.releaseKernel()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage, borderMode} = this.parameters
        sourceImage.addRef("Convolve.writeRegion - sourceImage")
        let kernelImage: HalImageRef | undefined
        let sourceHalImage: RegionLockedHalImageRef | undefined
        try {
            // update kernel before all else
            kernelImage = await this.getKernel(mipLevelRegion.mipLevel)

            // update source image
            sourceHalImage = await sourceImage.getUpdatedHalImage(this.determineSourceRegion(sourceImage, kernelImage, mipLevelRegion), options)

            this.ctx.convolveHelper.convolve({
                source: sourceHalImage.halImage,
                target: outputImage,
                kernelImage: kernelImage.ref,
                targetMipLevel: mipLevelRegion.mipLevel,
                targetRegion: mipLevelRegion,
                borderMode,
            })
        } finally {
            kernelImage?.release()
            sourceHalImage?.release()
            sourceImage.release("Convolve.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        const targetRegions = sourceRegions.flatMap((sourceRegion) => {
            const halfKernelSize = {
                width: (this.parameters.kernel.width - 1) / 2,
                height: (this.parameters.kernel.height - 1) / 2,
            }
            const mappedTargetRegion: Region = {
                x: sourceRegion.x - halfKernelSize.width,
                y: sourceRegion.y - halfKernelSize.height,
                width: sourceRegion.width + halfKernelSize.width * 2,
                height: sourceRegion.height + halfKernelSize.height * 2,
            }
            return applyBorderModeToRegion(this.descriptor, mappedTargetRegion, 0, this.parameters.borderMode)
        })
        this.invalidateRegions(targetRegions)
    }

    private determineSourceRegion(source: AnyImageOpHalData, kernel: HalImageRef, mipLevelRegion: MipLevelRegion): MipLevelRegion {
        if (!this.parameters) {
            throw new Error("No parameters set")
        }
        const kernelExtentX = (kernel.ref.width - 1) / 2
        const kernelExtentY = (kernel.ref.height - 1) / 2
        const sourceXn = Math.floor(mipLevelRegion.x - kernelExtentX)
        const sourceYn = Math.floor(mipLevelRegion.y - kernelExtentY)
        const sourceXp = Math.ceil(mipLevelRegion.x + mipLevelRegion.width - 1 + kernelExtentX)
        const sourceYp = Math.ceil(mipLevelRegion.y + mipLevelRegion.height - 1 + kernelExtentY)
        return {
            x: sourceXn,
            y: sourceYn,
            width: sourceXp - sourceXn + 1,
            height: sourceYp - sourceYn + 1,
            mipLevel: mipLevelRegion.mipLevel,
        }
    }

    private async getKernel(mipLevel: number) {
        const kernelByMipLevel = await this.getKernelByMipLevel()
        const kernelMipLevel = Math.min(kernelByMipLevel.length - 1, mipLevel) // TODO if we hit the ceiling of the kernel mip-maps we might need correction
        const kernel = kernelByMipLevel[kernelMipLevel]
        kernel.addRef()
        return kernel
    }

    private async getKernelByMipLevel() {
        if (isPromise(this.kernelByMipLevel)) {
            this.kernelByMipLevel = await this.kernelByMipLevel
        } else if (this.kernelByMipLevel.length === 0) {
            this.kernelByMipLevel = this.createKernel(this.parameters.kernel)
            this.kernelByMipLevel = await this.kernelByMipLevel
        }
        return this.kernelByMipLevel
    }

    private releaseKernel() {
        if (isPromise(this.kernelByMipLevel)) {
            this.kernelByMipLevel.then((kernelByMipLevel) => kernelByMipLevel.forEach((kernel) => kernel.release()))
        } else {
            this.kernelByMipLevel.forEach((kernel) => kernel.release())
        }
        this.kernelByMipLevel = []
    }

    private async createKernel(kernel: Kernel) {
        const kernelByMipLevel: HalImageRef[] = []
        while (true) {
            const mipLevel = kernelByMipLevel.length
            const mipLevelScale = Math.pow(2, -mipLevel)
            let kernelWidth = Math.ceil(kernel.width * mipLevelScale)
            let kernelHeight = Math.ceil(kernel.height * mipLevelScale)
            if (kernelWidth % 2 === 0) {
                kernelWidth++
            }
            if (kernelHeight % 2 === 0) {
                kernelHeight++
            }
            const kernelImage = this.ctx.createImagePhysical({
                width: kernelWidth,
                height: kernelHeight,
                channels: 1,
            })
            kernelByMipLevel.push(kernelImage)
            if (kernelWidth === 1 && kernelHeight === 1) {
                break
            }
        }
        await kernelByMipLevel[0].ref.writeImageData({
            sourceData: {
                width: kernel.width,
                height: kernel.height,
                channelLayout: "R",
                dataType: "float32",
                data: new Float32Array(kernel.values),
            },
        })
        const targetKernelSum = this.ctx.createImagePhysical({
            width: 1,
            height: 1,
            channels: 1,
        })
        this.ctx.reductionHelperSum.reduce({
            source: kernelByMipLevel[0].ref,
            target: targetKernelSum.ref,
        })
        const kernelSum = this.ctx.createImagePhysical({
            width: 1,
            height: 1,
            channels: 1,
        })
        const scale = this.ctx.createImagePhysical({
            width: 1,
            height: 1,
            channels: 1,
        })
        for (let i = 1; i < kernelByMipLevel.length; i++) {
            const source = kernelByMipLevel[i - 1]
            const target = kernelByMipLevel[i]
            const sourceOffset = {
                x: Math.ceil(source.ref.width / 2) - target.ref.width,
                y: Math.ceil(source.ref.height / 2) - target.ref.height,
            }
            this.ctx.mipmapHelper.generateMipMap({
                source: source.ref,
                target: target.ref,
                options: {
                    centered: true,
                    sourceOffset,
                },
            })
            // make sure we keep the sum of the kernel constant
            this.ctx.reductionHelperSum.reduce({
                source: target.ref,
                target: kernelSum.ref,
            })
            this.ctx.arithmeticHelper.div({
                operandA: targetKernelSum.ref,
                operandB: kernelSum.ref,
                result: scale.ref,
            })
            const rescaledTarget = this.ctx.createImagePhysical({
                width: target.ref.descriptor.width,
                height: target.ref.descriptor.height,
                channels: 1,
            })
            this.ctx.arithmeticHelper.mul({
                operandA: target.ref,
                operandB: scale.ref,
                result: rescaledTarget.ref,
            })
            target.release()
            kernelByMipLevel[i] = rescaledTarget
        }
        targetKernelSum.release()
        kernelSum.release()
        scale.release()
        return kernelByMipLevel
    }

    private kernelByMipLevel: HalImageRef[] | Promise<HalImageRef[]> = []
}
